import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Trophy, ArrowLeft, BookOpen, Award } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import CertificateCard from "@/components/certificates/CertificateCard";
import { SkelLine } from "@/components/Skeleton";

// A2 · Public certificate sort — options mirror the backend whitelist.
// Value === backend `sort` query param. Order also drives the dropdown.
const SORT_OPTIONS = [
  { value: "default",   label: "Default Order" },
  { value: "newest",    label: "Newest First" },
  { value: "oldest",    label: "Oldest First" },
  { value: "name_asc",  label: "Collector Name A → Z" },
  { value: "name_desc", label: "Collector Name Z → A" },
  { value: "cert_asc",  label: "Certificate Number Low → High" },
  { value: "cert_desc", label: "Certificate Number High → Low" },
];
const VALID_SORT_KEYS = new Set(SORT_OPTIONS.map((o) => o.value));

export default function Registry() {
  const [data, setData] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const urlSort = searchParams.get("sort") || "default";
  const sort = VALID_SORT_KEYS.has(urlSort) ? urlSort : "default";
  const urlSearch = searchParams.get("search") || "";
  const [q, setQ] = useState(urlSearch);

  // Fetch registry list (respects sort). Re-runs when sort changes or
  // when the search term is cleared. Search + sort share the URL so
  // reloads / shared links reproduce the exact view.
  useEffect(() => {
    if (urlSearch.trim()) {
      // Search branch takes over immediately below.
      return;
    }
    setSearchResults(null);
    api.get("/public/registry", { params: { sort } })
      .then((r) => setData(r.data))
      .catch(() => setData({ stats: { hardcover: 0, softcover: 0, total: 0 }, hardcover: [], softcover: [] }));
  }, [sort, urlSearch]);

  // Kick off the search whenever the URL carries a search term.
  useEffect(() => {
    const term = urlSearch.trim();
    if (!term) return;
    setSearchLoading(true);
    api.get("/public/registry/search", { params: { q: term, sort } })
      .then((r) => setSearchResults(r.data.results || []))
      .catch(() => setSearchResults([]))
      .finally(() => setSearchLoading(false));
  }, [urlSearch, sort]);

  const _updateParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (!v || v === "default") next.delete(k);
      else next.set(k, v);
    }
    setSearchParams(next, { replace: true });
  };

  const onSearch = (e) => {
    e.preventDefault();
    const term = q.trim();
    _updateParams({ search: term });
    if (!term) setSearchResults(null);
  };

  const clearSearch = () => {
    setQ("");
    _updateParams({ search: "" });
    setSearchResults(null);
  };

  const onSortChange = (value) => {
    _updateParams({ sort: value });
  };

  const stats = data?.stats || { hardcover: 0, softcover: 0, total: 0 };

  return (
    <div className="premium-wrap min-h-[80vh]" data-testid="registry-page">
      <SEO
        title="Certificates · Gil Maakaroun"
        description="The official verified certificates of hardcover and softcover Panini album completions independently inspected by Gil Maakaroun."
      />
      <div className="container-gm py-12 md:py-20">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/60 hover:text-[var(--gm-gold)] text-sm"
          data-testid="registry-back-home"
        >
          <ArrowLeft size={14} /> Back to the site
        </Link>

        {/* Hero */}
        <div className="mt-8 max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-[var(--gm-gold)]/40 bg-[var(--gm-gold)]/10 px-4 py-1.5 text-xs uppercase tracking-widest text-[var(--gm-gold)]">
            <Trophy size={14} /> Official Verified Albums
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-6 text-white leading-[1.05]" data-testid="registry-hero-title">
            Certificates
          </h1>
          <p className="text-white/70 mt-5 text-base sm:text-lg leading-relaxed">
            Each entry below has been personally inspected by Gil Maakaroun and
            recorded with a permanent, tamper-resistant certificate number.
          </p>
        </div>

        {/* Certificate Purchasing & Album-Verification banner —
            prominent CTA above the fold, does not cover the search
            or stats. */}
        <div className="mt-8" data-testid="registry-cert-order-banner">
          <div className="rounded-3xl border-2 border-[var(--gm-gold)] bg-gradient-to-r from-[rgba(230,184,72,0.12)] to-[rgba(230,184,72,0.02)] p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-[var(--gm-gold)] font-bold">Album Complete Certificate</div>
              <div className="mt-1 font-serif text-2xl md:text-3xl text-white">Completed Your Album? Get Officially Certified.</div>
              <p className="mt-2 text-sm text-white/80 max-w-2xl">
                Order your official CollectorUp Album Complete Certificate.
                After ordering, send a short album-verification video through
                WhatsApp. Once approved, your certificate will be prepared and
                shipped.
              </p>
              <div className="mt-3 text-xs text-white/60 flex flex-wrap gap-x-4 gap-y-1" data-testid="registry-banner-breakdown">
                <span>Certificate <strong className="text-white">$5.00</strong></span>
                <span>Shipping <strong className="text-white">$5.00</strong></span>
                <span>Tax <strong className="text-white">$0.70</strong></span>
                <span>Total <strong className="text-[var(--gm-gold)]">$10.70</strong></span>
              </div>
            </div>
            <a
              href="/certificates/order"
              data-testid="registry-banner-cta"
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[var(--gm-gold)] text-[var(--ink)] font-display uppercase tracking-widest text-sm px-5 py-3 hover:brightness-110 transition"
            >
              Order Your Certificate
            </a>
          </div>
        </div>

        {/* Live stats */}
        <div className="grid gap-4 sm:grid-cols-3 mt-10" data-testid="registry-stats">
          <StatCard icon={<BookOpen size={20} />} label="Hardcover Albums" value={stats.hardcover} testid="stat-hc" />
          <StatCard icon={<BookOpen size={20} />} label="Softcover Albums" value={stats.softcover} testid="stat-sc" />
          <StatCard icon={<Award size={20} />} label="Total Verified Albums" value={stats.total} accent testid="stat-total" />
        </div>

        {/* Search + Sort */}
        <div className="mt-10 flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6" data-testid="registry-controls">
          <form
            onSubmit={onSearch}
            className="flex flex-col sm:flex-row gap-3 flex-1 max-w-2xl"
            data-testid="registry-search-form"
          >
            <div className="relative flex-1">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by certificate number or collector name…"
                className="w-full rounded-full border border-white/15 bg-white/5 py-3 pl-11 pr-4 text-white placeholder:text-white/40 focus:border-[var(--gm-gold)] focus:outline-none"
                data-testid="registry-search-input"
              />
            </div>
            <button
              type="submit"
              className="btn-gold px-6 py-3 text-sm"
              disabled={searchLoading || !q.trim()}
              data-testid="registry-search-submit"
            >
              {searchLoading ? "Searching…" : "Search"}
            </button>
            {searchResults !== null && (
              <button
                type="button"
                onClick={clearSearch}
                className="text-sm text-white/50 hover:text-white underline underline-offset-4"
                data-testid="registry-search-clear"
              >
                Clear
              </button>
            )}
          </form>

          {/* A2 · Sort control — public certificate ordering. Keeps the
              existing default when set to `default`; every non-default
              value round-trips through the URL so links can be shared. */}
          <label
            className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 py-2 px-4 text-sm text-white/70 shrink-0"
            data-testid="registry-sort-wrapper"
          >
            <span className="uppercase tracking-widest text-[10px] text-white/50">Sort by</span>
            <select
              value={sort}
              onChange={(e) => onSortChange(e.target.value)}
              className="bg-transparent text-white text-sm focus:outline-none appearance-none pr-1 cursor-pointer"
              data-testid="registry-sort-select"
              aria-label="Sort certificates"
            >
              {SORT_OPTIONS.map((opt) => (
                <option
                  key={opt.value}
                  value={opt.value}
                  className="bg-[var(--ink)] text-white"
                  data-testid={`registry-sort-option-${opt.value}`}
                >
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Search results OR HC/SC sections */}
        {searchResults !== null ? (
          <SearchResults rows={searchResults} query={q} />
        ) : data === null ? (
          <div className="mt-14" data-testid="registry-loading">
            <SkelLine className="h-8 w-64 mb-4" />
            <SkelLine className="h-4 w-full mb-2" />
            <SkelLine className="h-4 w-3/4" />
          </div>
        ) : (
          <>
            <RegistrySection
              title="Hardcover Albums"
              rows={data.hardcover}
              testid="section-hardcover"
              accent="hc"
              emptyText="No hardcover albums have been verified yet."
            />
            <RegistrySection
              title="Softcover Albums"
              rows={data.softcover}
              testid="section-softcover"
              accent="sc"
              emptyText="No softcover albums have been verified yet."
            />
          </>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, accent, testid }) {
  return (
    <div
      className={
        "rounded-2xl border p-6 " +
        (accent
          ? "border-[var(--gm-gold)]/40 bg-[var(--gm-gold)]/8"
          : "border-white/10 bg-white/5")
      }
      data-testid={testid}
    >
      <div className={"inline-flex items-center gap-2 text-xs uppercase tracking-widest " + (accent ? "text-[var(--gm-gold)]" : "text-white/50")}>
        {icon}
        {label}
      </div>
      <div className="mt-3 font-serif text-4xl text-white" data-testid={`${testid}-value`}>
        {value}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
function RegistrySection({ title, rows, testid, accent, emptyText }) {
  return (
    <section className="mt-14" data-testid={testid}>
      <div className="flex items-center gap-3 mb-5">
        <div className={"h-3 w-3 rounded-full " + (accent === "hc" ? "bg-[var(--gm-gold)]" : "bg-[var(--panini-red)]")} />
        <h2 className="font-serif text-2xl text-white">{title}</h2>
        <span className="text-white/40 text-sm">({rows.length})</span>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-white/50 text-center text-sm" data-testid={`${testid}-empty`}>
          {emptyText}
        </div>
      ) : (
        <CertificateCardGrid rows={rows} testid={`${testid}-grid`} />
      )}
    </section>
  );
}

// ────────────────────────────────────────────────────────────────
function CertificateCardGrid({ rows, testid }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid={testid}>
      {rows.map((r) => (
        <CertificateCard
          key={r.cert_number}
          cert_number={r.cert_number}
          collector_name={r.collector_name}
          album_slug={r.album_slug}
          album_type={r.album_type}
          completion_date={r.completion_date}
          is_claimed={r.is_claimed}
          owner_username={r.owner_username}
          owner_display_name={r.owner_display_name}
          owner_avatar_media_id={r.owner_avatar_media_id}
          owner_avatar_updated_at={r.owner_avatar_updated_at}
          owner_profile_public={r.owner_profile_public}
          verified
          theme="dark"
        />
      ))}
    </div>
  );
}

function SearchResults({ rows, query }) {
  if (!rows.length) {
    return (
      <div className="mt-14 rounded-2xl border border-white/10 bg-white/5 p-10 text-center" data-testid="registry-search-empty">
        <div className="text-white/60 text-sm">
          No public certificates match <span className="font-mono text-white">&quot;{query}&quot;</span>.
        </div>
        <p className="text-white/40 text-xs mt-3">Search covers certificate number and collector name only.</p>
      </div>
    );
  }
  return (
    <section className="mt-14" data-testid="registry-search-results">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-3 w-3 rounded-full bg-white/60" />
        <h2 className="font-serif text-2xl text-white">
          Search Results
        </h2>
        <span className="text-white/40 text-sm">({rows.length})</span>
      </div>
      <CertificateCardGrid rows={rows} testid="search-results-grid" />
    </section>
  );
}
